import { useEffect, useState } from 'react';
import { Button, Divider, Group, Modal, Stack, Switch } from '@mantine/core';
import styles from './EditFormModal.module.sass';
import type { FormView } from '@/types/form';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';

type EditFormModalProps = {
  opened: boolean;
  formData: FormView | null;
  onClose: () => void;
  onSave: (data: FormView) => Promise<void> | void;
};

export const EditFormModal = ({ opened, formData, onClose, onSave }: EditFormModalProps) => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  useEffect(() => {
    if (opened && formData) {
      setIsActive(formData.isActive);
    }
  }, [opened, formData]);

  const handleSave = async () => {
    if (!formData) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedData: FormView = {
        ...formData,
        isActive,
      };

      await onSave(updatedData);
    } catch (error: unknown) {
      console.error('Failed to save form data:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Form Information"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
      size="lg"
    >
      <Stack gap="md" pt="md">
        <Stack gap="xs">
          <Switch
            label="Form Active"
            labelPosition="left"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />
        </Stack>

        <Divider my="xs" />
        <SaveErrorBox error={saveError} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving} color="primary">
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
